import React, { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { TextInput } from "App/common/TextInput";
import { highnote } from "@highnote/server/src/sdk";
import { ToastMessageContent, useToast } from "App/common/useToast";
import {
  DELETE_FIRESTORE_FIELD,
  FileEntity,
  Space,
} from "@highnote/server/src/core/entities";
import { ErrorBoundary } from "App/common/ErrorBoundary";
import { Button, BUTTON_THEME } from "App/core/Button";
import { useAuth } from "App/components/Auth";
import {
  TRACK_NAME_ALREADY_EXISTS_ERROR,
  PERMISSION,
} from "@highnote/server/src/core/shared-util";
import { PermissionTooltip } from "App/components/PermissionTooltip";
import { useTrack } from "App/components/useEntities/useTrack";
import { DialogButtons, DialogInfo, DialogSection } from "App/common/Dialog";
import moment from "moment";
import { ConnectedUserName } from "App/common/UserAvatar/UserAvatar";
import { EditableArtwork } from "App/components/EditableArtwork";
import { useSpaceContext } from "App/common/useSpace";
import { useGlobalSpaces } from "App/store/spaces/useGlobalSpaces";
import { useSpaceTrackRow } from "App/store/spaceTracks/useSpaceTrackRow";
import { ViewingSpaceId } from "App/modules/viewingSpace";

type TrackEditState = {
  title: string;
  description: string;
  artistName: string;
  file: FileEntity;
};

export const TrackDetails = ({
  space,
  viewingSpaceId,
}: {
  /** The track's ORIGIN space (what `useTrackEditor` resolves from
   *  `track.spaceId`). Used only when there is no viewing space. */
  space?: Space;
  /** The space the editor was opened FROM. When set, the four fields edit
   *  THIS space's alias of the track (decision 3, 2026-09-07); the server
   *  decides whether that is the origin (writes track + row) or a
   *  borrowing space (writes the row only). */
  viewingSpaceId?: ViewingSpaceId;
}) => {
  const { isAllowed } = useAuth();
  const { track, trackArtworkUrl, setTrackArtworkUrl } = useTrack();
  const { space: routeSpace, spaceId: routeSpaceId } = useSpaceContext();
  const { getGlobalSpace } = useGlobalSpaces();
  const viewingSpace = !viewingSpaceId
    ? undefined
    : viewingSpaceId === routeSpaceId
      ? routeSpace
      : getGlobalSpace(viewingSpaceId);
  const row = useSpaceTrackRow(viewingSpaceId, track?.id);
  const { toasted, addMessage } = useToast();
  const [artworkUrl, setArtworkUrl] = useState(trackArtworkUrl);
  const [trackState, setTrackState] = useState<TrackEditState>({
    title: track.title || "",
    description: track.description || "",
    artistName: track.artistName || "",
    file:
      track.artworkFile &&
      ({
        id: track.artworkFile,
      } as FileEntity),
  });
  const canEditTrack = viewingSpaceId
    ? isAllowed(PERMISSION.TO_EDIT_SPACE_TRACK, {
        space: viewingSpace,
        track,
        spaceTrackRow: row,
      })
    : isAllowed(PERMISSION.TO_EDIT_TRACK_IN_SPACE, { space, track });
  const unmountedRef = useRef<boolean>(false);
  // Set by the three text fields' onChange, cleared after a successful save.
  // While false, the re-seed effect below keeps the form in sync with a
  // `track` that can still change out from under it (the viewing space's
  // row arrives asynchronously, after this component may have mounted).
  const dirtyRef = useRef<boolean>(false);
  // The artwork picker has its own flag: a picked image must survive a late
  // row arrival, but picking one must not stop the text re-seed below.
  const artworkDirtyRef = useRef<boolean>(false);

  useEffect(
    () => () => {
      unmountedRef.current = true;
    },
    [],
  );

  useEffect(() => {
    if (track?.artworkFile) {
      setTrackState((oldState) => {
        return {
          ...oldState,
          file: { id: track.artworkFile } as FileEntity,
        };
      });
      return;
    }
    // No artwork in this space (the row applied `null`, or the track has
    // none). While the form is untouched, drop the seeded file too — a
    // Save that resent the canonical id would otherwise fail on the server's
    // "already parented" check. Same rule as the text re-seed below.
    if (!artworkDirtyRef.current) {
      setTrackState((oldState) => ({ ...oldState, file: undefined }));
    }
  }, [track?.artworkFile]);

  // Re-seed the text fields from `track` as long as the user has not typed
  // into the form. This covers the case where the editor mounts before the
  // viewing space's row is applied to `track` (a deep link, or a space
  // watcher that has not attached yet): the initial useState above seeds the
  // canonical value, and this effect corrects it to the alias once the row
  // arrives — without clobbering anything the user has already edited.
  useEffect(() => {
    if (dirtyRef.current) return;
    setTrackState((oldState) => ({
      ...oldState,
      title: track?.title || "",
      description: track?.description || "",
      artistName: track?.artistName || "",
    }));
  }, [track?.id, track?.title, track?.description, track?.artistName]);

  useEffect(() => {
    if (trackArtworkUrl) {
      setArtworkUrl(trackArtworkUrl);
    }
  }, [trackArtworkUrl]);

  const onSubmit = async () => {
    if (!trackState.title) {
      const toastId = uuidv4();

      addMessage({
        id: toastId,
        children: (
          <ToastMessageContent
            type="error"
            id={toastId}
            text={`Track must have a name.`}
          />
        ),
      });

      return;
    }

    // `file` is null only when the user pressed remove. `undefined` means
    // "unchanged": the callable transport drops an undefined key, so the
    // server sees no artwork field at all. Remove used to send `undefined`
    // and therefore did nothing, on both paths.
    const artworkCleared = trackState.file === null;
    const shared = {
      title: trackState.title,
      description: trackState.description || null,
      artistName: trackState.artistName,
    };
    const requestUpdate = async () => {
      if (viewingSpaceId) {
        await highnote.updateSpaceTrack({
          spaceId: viewingSpaceId,
          trackId: track.id,
          data: {
            ...shared,
            artworkFile: artworkCleared ? null : trackState.file?.id,
          },
        });
        return;
      }
      await highnote.updateTrack({
        id: track.id,
        data: {
          ...shared,
          // A row clears a field with null; tracks-v2 clears it with the
          // delete sentinel. UPDATE_SPACE_TRACK converts on the way in;
          // this path talks to tracks-v2 directly, so it converts here.
          artworkFile: artworkCleared
            ? DELETE_FIRESTORE_FIELD
            : trackState.file?.id,
        },
      });
    };

    try {
      await toasted({
        promise: requestUpdate(),
        createMessage: "Updating track...",
        successMessage: "Track saved.",
        errorMessage: "Could not update track. Please try again.",
        ErrorContent: ({ error }) => {
          if (error.message.includes(TRACK_NAME_ALREADY_EXISTS_ERROR)) {
            return <>{error.message}</>;
          }

          return <>Could not update track. Please try again.</>;
        },
      });
      dirtyRef.current = false;
      artworkDirtyRef.current = false;
    } catch (error) {
      if (error.message.includes(TRACK_NAME_ALREADY_EXISTS_ERROR)) {
        setTrackState({ ...trackState, title: track.title });
      }
    }

    setTrackArtworkUrl(artworkUrl);
  };

  const createdAtStr = moment(track.createdAt).fromNow();

  return (
    <ErrorBoundary name="TrackDetails">
      <div className="highnote-track-details">
        <DialogSection>
          <h3>Track Artwork</h3>

          <EditableArtwork
            disabled={!canEditTrack}
            artworkUrl={artworkUrl}
            fallbackArtworkUrl={"/public/default-track-artwork.png"}
            onChange={async (file, tempUrl) => {
              artworkDirtyRef.current = true;
              setArtworkUrl(tempUrl);
              setTrackState({ ...trackState, file });
            }}
          />
        </DialogSection>

        <DialogSection>
          <h3>Track Name</h3>
          <PermissionTooltip hasPermission={canEditTrack}>
            <div className="input-field">
              <TextInput
                isSingleLine
                isReadOnly={!canEditTrack}
                value={trackState.title}
                required
                placeholder="New Track"
                onChange={(val) => {
                  dirtyRef.current = true;
                  setTrackState({ ...trackState, title: val });
                }}
              />
            </div>
          </PermissionTooltip>
        </DialogSection>

        <DialogSection>
          <h3>Track Artist</h3>
          <PermissionTooltip hasPermission={canEditTrack}>
            <div className="input-field">
              <TextInput
                isSingleLine
                isReadOnly={!canEditTrack}
                value={trackState.artistName || ""}
                placeholder="Track Artist"
                onChange={(val) => {
                  dirtyRef.current = true;
                  setTrackState({ ...trackState, artistName: val });
                }}
              />
            </div>
          </PermissionTooltip>
        </DialogSection>

        <DialogSection>
          <h3>Description</h3>
          <PermissionTooltip hasPermission={canEditTrack}>
            <div className="input-field description-field">
              <TextInput
                isReadOnly={!canEditTrack}
                value={trackState.description}
                placeholder="Track Description"
                onChange={(val) => {
                  dirtyRef.current = true;
                  setTrackState({ ...trackState, description: val });
                }}
              />
            </div>
          </PermissionTooltip>
        </DialogSection>

        <DialogButtons>
          <Button
            theme={BUTTON_THEME.CTA}
            disabled={!canEditTrack}
            onClick={onSubmit}
          >
            Save Changes
          </Button>
        </DialogButtons>
      </div>

      <DialogInfo>
        Created {createdAtStr} by <ConnectedUserName userId={track.createdBy} />
      </DialogInfo>
    </ErrorBoundary>
  );
};
